import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import FreeSpinSelect from '../../../../../components/shared/FreeSpinSelect';
import { useTranslation } from 'react-i18next';
import useToast from '../../../../../hooks/useToast';
import useBootstrapSize from '../../../../../hooks/useBootstrapSize';
import CurrencySelect from '../../../../../components/shared/CurrencySelect';
import FSInfoString from '../../../freespin/ui/FSInfoString';
import BtnTrash from '../../../../../components/bonus/ui/BtnTrash';
import Card from '../../../../../components/material-ui/adaptive/Card';
import CusInput from '../../../../../components/material-ui/CusInput';
import CusDatePicker from '../../../../../components/material-ui/CusDatePicker';
import FlexContainer from '../../../../../components/material-ui/adaptive/FlexContainer';
import ErrorBoundary from '../../../../../components/material-ui/error_boundary/ErrorBoundary';
import ShowContainer from '../../../../../components/material-ui/ShowContainer';
import BtnPlus from '../../../../../components/bonus/ui/BtnPlus';
import CusLink from '../../../../../components/material-ui/CusLink';
import { useAppSelector } from '../../../../../store';
import CusButton from '../../../../../components/material-ui/CusButton';
import { useLocation } from 'react-router';

export default function FSBonusAdd({
  groupId,
  addNewCampaign,
  fsName,
  fsCurrency,
  fsActivated,
  fsExpired,
  changeFsCurrency,
  changeFsActivated,
  changeFsExpired,
  fsCampaigns,
  deleteCampaign,
  changeFsName,
  setOpenInfoId,
  allCampaigns
}) {
  const { t } = useTranslation();
  const { errorToast } = useToast();
  const { isMobile, isTablet } = useBootstrapSize();
  const campaignsGroup = useAppSelector((state) => state.freeSpin.campaignByGroupId?.[groupId]);
  const { pathname } = useLocation();

  const defNewBonus = { campaignId: '', minDeposit: '', maxDeposit: '' };

  const [newBonusInfo, setNewBonus] = useState(defNewBonus);
  const saveDisabled = !(
    newBonusInfo.campaignId &&
    newBonusInfo.minDeposit &&
    newBonusInfo.minDeposit <= newBonusInfo.maxDeposit
  );

  // при смене валюты удалять те которые созданы в отличной от текущей валюты
  useEffect(() => {
    for (let indx in fsCampaigns) {
      const campaignId = fsCampaigns[indx].campaignId;

      const campaign = campaignsGroup.find((el) => el.campaignId == campaignId);
      if (campaign.currency !== fsCurrency) deleteCampaign(indx);
    }
  }, [fsCurrency, fsCampaigns, campaignsGroup]);

  const onPlusClick = () => {
    if (!newBonusInfo.campaignId) errorToast('select campaign');

    addNewCampaign(newBonusInfo);
    setNewBonus(defNewBonus);
  };

  return (
    <>
      <Card>
        <Grid container spacing={isMobile ? 2 : 1} justifyContent={'space-between'} mt={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }} order={{ xs: 0 }}>
            <CusInput
              label={'Bonus name'}
              id={'bonus-name'}
              value={fsName}
              tooltipText={'The name of the bonus is visible to players.'}
              onChange={(e) => changeFsName(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 1.5 }} pt={1} alignSelf={'center'} order={{ xs: 2 }}>
            <Typography textAlign={'end'}>{t('Available from')}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.5 }} alignSelf={'end'} order={{ xs: 2 }}>
            <CusDatePicker
              label="Start Date"
              value={fsActivated}
              onChange={(e) => changeFsActivated(e)}
              time={'00:00:00'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.5 }} alignSelf={'end'} order={{ xs: 3, md: 3 }}>
            <CusDatePicker
              label="End Date"
              value={fsExpired}
              onChange={(e) => changeFsExpired(e)}
              time={'23:59:59'}
              errorText={
                fsActivated && fsExpired && fsActivated > fsExpired
                  ? t('End date must be later than start date')
                  : ''
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }} order={{ xs: 1, md: 4 }}>
            <CurrencySelect
              id={'freespin_currency_add'}
              value={fsCurrency}
              setValue={changeFsCurrency}
            />
          </Grid>
        </Grid>
        <Stack gap={2} mt={2}>
          <Stack
            display={'flex'}
            flexDirection={'row'}
            alignItems={'end'}
            justifyContent={'space-between'}
            gap={1}>
            <FreeSpinSelect
              id={'freespin_fs_add'}
              value={newBonusInfo.campaignId}
              setValue={(val) => setNewBonus((prev) => ({ ...prev, campaignId: val }))}
              groupId={groupId}
              currency={fsCurrency}
              plugNoCampaign={
                <Stack gap={2} display={'flex'} flexDirection={'row'}>
                  <span>{t('no freespin templates')}</span>
                  <CusLink to={`/freespin/${groupId}/new`} text="Create a new template" />
                </Stack>
              }
            />

            <ShowContainer condition={newBonusInfo.campaignId}>
              <CusButton
                style={{ width: 'max-content' }}
                text="view campaign"
                onClick={() =>
                  setOpenInfoId({ value: newBonusInfo.campaignId, type: 'campaignId' })
                }
              />
            </ShowContainer>
          </Stack>
          <Box gap={1}>
            <Typography variant="body1" color="textSecondary">
              {t('conditions for receiving freespins')}
            </Typography>

            <Grid container alignItems={'center'} spacing={1} mt={1}>
              <Grid size={{ xs: 12, md: 'auto' }}>
                <Typography variant="subtitle2"> {t('deposit from')} </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <FlexContainer alignItems={isMobile ? undefined : 'center'} gap={isMobile ? 0 : 1}>
                  <CusInput
                    value={newBonusInfo.minDeposit}
                    onChange={(e) => {
                      setNewBonus((prev) => ({ ...prev, minDeposit: +e.target.value }));
                    }}
                    inputProps={{ min: 1 }}
                    type="number"
                    hideArrows
                  />
                  <Typography variant="subtitle2"> {t('to')} </Typography>
                  <CusInput
                    value={newBonusInfo.maxDeposit}
                    onChange={(e) =>
                      setNewBonus((prev) => ({ ...prev, maxDeposit: +e.target.value }))
                    }
                    inputProps={{ min: 0 }}
                    type="number"
                    hideArrows
                  />
                </FlexContainer>
              </Grid>
              <Grid size={{ xs: 6, sm: 'auto' }}>
                {isTablet ? (
                  <BtnPlus onPlusClick={onPlusClick} disabled={saveDisabled} />
                ) : (
                  <Button
                    variant="outlined"
                    onClick={onPlusClick}
                    fullWidth={isMobile}
                    disabled={saveDisabled}>
                    {t('Add')}
                  </Button>
                )}
              </Grid>

              <ShowContainer condition={campaignsGroup?.length}>
                <Grid size={{ xs: 6, sm: 'auto' }} ml={'auto'}>
                  <CusLink
                    to={`/freespin/${groupId}/new?prev=${pathname}`}
                    text="Create a new template"
                  />
                </Grid>
              </ShowContainer>
            </Grid>
          </Box>
        </Stack>
      </Card>

      <ErrorBoundary>
        <ShowContainer condition={fsCampaigns.length}>
          <Stack gap={1} padding={2}>
            {fsCampaigns.map((company, indx) => {
              const currencyCompany =
                allCampaigns?.find((el) => el.campaignId == company.campaignId) || {};

              return (
                <Stack
                  key={indx}
                  direction={'row'}
                  justifyContent={'space-between'}
                  alignItems={'center'}>
                  <FSInfoString
                    company={currencyCompany}
                    maxDeposit={company.maxDeposit}
                    minDeposit={company.minDeposit}
                  />
                  <BtnTrash width={150} onDelete={() => deleteCampaign(indx)} fullWidth={false} />
                </Stack>
              );
            })}
          </Stack>
        </ShowContainer>
      </ErrorBoundary>
    </>
  );
}
